import {enumIsEnumValue, enumReduce, objectFromEntries, Values} from '@wix/devzai-utils-common';
import {DoppePremiumFeatures, DoppePremiumFeaturesAlias, DoppePremiumFeaturesSpec} from './doppe-premium-features';
import {DoppePremiumBillingCycles, DoppePremiumFeaturesStatus, DoppePremiumProducts} from './doppe-premium';
import {
    wixGetPremiumProductPurchasableCycles,
    wixPremiumBooleanFeatureStatusCreate,
    wixPremiumFeaturesStatusCreateFullyDisabled,
    wixPremiumFeaturesStatusCreateFullyEnabled,
    WixPremiumFeatureStatus, WixPremiumProductsPricing,
    wixPremiumQuotaFeatureStatusCreate
} from '@wix/devzai-common-wix';

export const DoppeFreemiumPlan = {
    None: '00000000-0000-0000-0000-000000000000',
    /**
     * For users that joined before we had premium.
     */
    Legacy: 'fcfa9bc2-3f4c-49aa-b7cd-0a443312560a',
    /**
     * For users that joined after we had premium.
     */
    Default: 'fe6817ba-99b9-455c-b24f-c579b186d908',
    /**
     * For users that have multiple premium packages rather than only one.
     */
    MultiplePackagesOld: 'fa397e3b-41e9-4278-9adf-0d057a3dd23b',
    MultiplePackages: 'eb55a529-17e7-402a-bf4d-f122c342119a',
    /**
     * For users that have one premium packages and are coming from Google.
     */
    GoogleOnePlan: 't1e3e323-1129-r228-8cdf-uf86753dd981',
    /**
     * For users that have multiple premium packages and are coming from Google.
     */
    GoogleMultiplePackages: 'd2e3e32r-u123-cg24-bcd8-v6ew753dd9fc',
    /**
     * This freemium plan is for testing purposes ONLY. Feel free to change the features
     * that assigned to it.
     */
    FreemiumTesting: '8b23c6fa-0a22-40c6-bee1-15089287c8c5',
    /**
     * For users that have isracard plan.
     */
    Isracard: 'ea2d726f-fd0a-4608-b439-4e59728ecd99',
    /**
     * For users that have multiple premium packages rather than only one.
     */
    FreeTrial14: 'f4jc7e3b-1de9-2q78-8adf-1d9uya3dd2cf',
    FreeTrial14NoFreeVersion: '54jc7e6b-1de9-1q7v-4adf-rdeeya3td1cf',
    FreeTrial14WithViewerDisabled: '4af4a039-48af-4ac8-896b-b686d69ae70a',
    SiteFreeTrial14: '144c7e32-ytre-1a7u-1ady-4d9hga3dd29i',
    SiteBusiness: 'b4c7e22y-1de9-1q7a-4afd-1d9uy72bd2af',
} as const;

export type DoppeFreemiumPlan = Values<typeof DoppeFreemiumPlan>;

export const DoppeFreemiumPlanWithSinglePackage = enumReduce(DoppeFreemiumPlan, [
    DoppeFreemiumPlan.Legacy,
    DoppeFreemiumPlan.Default,
    DoppeFreemiumPlan.GoogleOnePlan,
]);

export type DoppeFreemiumPlanWithSinglePackage = Values<typeof DoppeFreemiumPlanWithSinglePackage>;

export const DoppeFreemiumPlanWithMultiplePackages = enumReduce(DoppeFreemiumPlan, [
    DoppeFreemiumPlan.MultiplePackagesOld,
    DoppeFreemiumPlan.MultiplePackages,
    DoppeFreemiumPlan.FreemiumTesting,
    DoppeFreemiumPlan.GoogleMultiplePackages,
    DoppeFreemiumPlan.Isracard,
    DoppeFreemiumPlan.FreeTrial14,
    DoppeFreemiumPlan.FreeTrial14NoFreeVersion,
    DoppeFreemiumPlan.FreeTrial14WithViewerDisabled,
    DoppeFreemiumPlan.SiteFreeTrial14,
    DoppeFreemiumPlan.SiteBusiness
]);

export type DoppeFreemiumPlanWithMultiplePackages = Values<typeof DoppeFreemiumPlanWithMultiplePackages>;

export const DoppeFreemiumPlanWithFreeTrial = enumReduce(DoppeFreemiumPlan, [
    DoppeFreemiumPlan.FreeTrial14,
    DoppeFreemiumPlan.FreeTrial14NoFreeVersion,
    DoppeFreemiumPlan.FreeTrial14WithViewerDisabled,
    DoppeFreemiumPlan.SiteFreeTrial14,
    DoppeFreemiumPlan.SiteBusiness
]);

export type DoppeFreemiumPlanWithFreeTrial = Values<typeof DoppeFreemiumPlanWithFreeTrial>;

export const DoppeFreemiumPlanBehaviours = {
    HideDashboard: 'HideDashboard',
    DisableViewer: 'DisableViewer',
} as const;

export type DoppeFreemiumPlanBehaviours = Values<typeof DoppeFreemiumPlanBehaviours>;

export namespace DoppeFreemiumPlan {
    export type Enabled = Exclude<DoppeFreemiumPlan, typeof DoppeFreemiumPlan.None>
}

export function doppeFreemiumPlanGetFreeTrialBehaviours (freemiumPlan: DoppeFreemiumPlan) : DoppeFreemiumPlanBehaviours[] {

    switch (freemiumPlan) {
        case DoppeFreemiumPlan.FreeTrial14NoFreeVersion: {
            return [DoppeFreemiumPlanBehaviours.HideDashboard, DoppeFreemiumPlanBehaviours.DisableViewer];
        }
        case DoppeFreemiumPlan.FreeTrial14WithViewerDisabled: {
            return [DoppeFreemiumPlanBehaviours.DisableViewer];
        }
        default: {
            return [];
        }
    }
}

export function doppeFreemiumPlanIsFreeTrialBehaviourActive(freemiumPlan: DoppeFreemiumPlan, behaviour: DoppeFreemiumPlanBehaviours) : boolean {
    const behaviours = doppeFreemiumPlanGetFreeTrialBehaviours(freemiumPlan);
    return behaviours.includes(behaviour);
}



export function doppeFreemiumPlanIsEnabled (freemiumPlan: DoppeFreemiumPlan) : freemiumPlan is DoppeFreemiumPlan.Enabled {
    return freemiumPlan !== DoppeFreemiumPlan.None;
}

function createPartialPremiumFeaturesStatus (
    statusesArr: WixPremiumFeatureStatus[]
) : Partial<DoppePremiumFeaturesStatus> {
    return objectFromEntries(statusesArr.map(status => [status.uniqueName, status]))
}

const commonPremiumFeaturesStatus = createPartialPremiumFeaturesStatus([
    wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.identity_collaborators, {limit: 1}),
])

const doppeFreemiumPlanFeaturesStatusCreateForMultiplePackagesFreemiumPlan = () => {
    return {
        ...wixPremiumFeaturesStatusCreateFullyDisabled(DoppePremiumFeaturesSpec),
        ...commonPremiumFeaturesStatus,
        ...createPartialPremiumFeaturesStatus([
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_add_items_to_list_action, {limit: 4}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_unlimited_pages, {limit: 1}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.creators_pre_rolls_count, {limit: 0}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_create_short_links, {limit: 0}),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_short_links, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_landing_pages, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_pre_rolls, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeaturesAlias.bannerAction, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, true),
        ])
    }
}

const doppeFreemiumPlanFeaturesStatusCreateForMultiplePackageFreemiumPlanNoFreeVersion = () => {
    return {
        ...wixPremiumFeaturesStatusCreateFullyDisabled(DoppePremiumFeaturesSpec),
        ...commonPremiumFeaturesStatus,
        ...createPartialPremiumFeaturesStatus([
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_add_items_to_list_action, {limit: 0}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_unlimited_pages, {limit: 0}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.creators_pre_rolls_count, {limit: 0}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_create_short_links, {limit: 0}),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_short_links, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_landing_pages, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_pre_rolls, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeaturesAlias.bannerAction, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, false),
        ])
    }
}

const DoppeFreemiumPlanFeaturesStatus = {
    [DoppeFreemiumPlan.Legacy]: {
        ...wixPremiumFeaturesStatusCreateFullyEnabled(DoppePremiumFeaturesSpec),
        ...createPartialPremiumFeaturesStatus([
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_remove_banner, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_advanced_design_options, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeaturesAlias.bannerAction, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, true),
        ])
    },
    [DoppeFreemiumPlan.Default]: {
        ...wixPremiumFeaturesStatusCreateFullyDisabled(DoppePremiumFeaturesSpec),
        ...commonPremiumFeaturesStatus,
        ...createPartialPremiumFeaturesStatus([
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_add_items_to_list_action, {limit: 4}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_unlimited_pages, {limit: 1}),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_landing_pages, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeaturesAlias.bannerAction, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, true),
        ])
    },
    [DoppeFreemiumPlan.MultiplePackagesOld]: {
        ...wixPremiumFeaturesStatusCreateFullyDisabled(DoppePremiumFeaturesSpec),
        ...commonPremiumFeaturesStatus,
        ...createPartialPremiumFeaturesStatus([
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_add_items_to_list_action, {limit: 4}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_unlimited_pages, {limit: 1}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.creators_pre_rolls_count, {limit: 0}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_create_short_links, {limit: 0}),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_short_links, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_landing_pages, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_pre_rolls, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeaturesAlias.bannerAction, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, true),
        ])
    },
    [DoppeFreemiumPlan.GoogleMultiplePackages]: {
        ...wixPremiumFeaturesStatusCreateFullyDisabled(DoppePremiumFeaturesSpec),
        ...commonPremiumFeaturesStatus,
        ...createPartialPremiumFeaturesStatus([
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_add_items_to_list_action, {limit: 4}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_unlimited_pages, {limit: 1}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.creators_pre_rolls_count, {limit: 0}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_create_short_links, {limit: 0}),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_short_links, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_landing_pages, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_pre_rolls, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeaturesAlias.bannerAction, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, true),
        ])
    },
    [DoppeFreemiumPlan.GoogleOnePlan]: {
        ...wixPremiumFeaturesStatusCreateFullyDisabled(DoppePremiumFeaturesSpec),
        ...commonPremiumFeaturesStatus,
        ...createPartialPremiumFeaturesStatus([
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_add_items_to_list_action, {limit: 4}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_unlimited_pages, {limit: 1}),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_landing_pages, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeaturesAlias.bannerAction, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, true),
        ])
    },
    /**
     * This freemium plan is for testing purposes ONLY. Feel free to change the features
     * that assigned to it.
     */
    [DoppeFreemiumPlan.FreemiumTesting]: {
        ...wixPremiumFeaturesStatusCreateFullyDisabled(DoppePremiumFeaturesSpec),
        ...commonPremiumFeaturesStatus,
        ...createPartialPremiumFeaturesStatus([
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_add_items_to_list_action, {limit: 4}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_unlimited_pages, {limit: 1}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.creators_pre_rolls_count, {limit: 1}),
            wixPremiumQuotaFeatureStatusCreate(DoppePremiumFeatures.hopp_create_short_links, {limit: 1}),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_short_links, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_landing_pages, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_allow_pre_rolls, true),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_form_submission, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.creators_monetization_actions, false),
            wixPremiumBooleanFeatureStatusCreate(DoppePremiumFeatures.hopp_full_screen_view, true),
        ])
    },
    [DoppeFreemiumPlan.MultiplePackages]: doppeFreemiumPlanFeaturesStatusCreateForMultiplePackagesFreemiumPlan(),
    [DoppeFreemiumPlan.Isracard]: doppeFreemiumPlanFeaturesStatusCreateForMultiplePackagesFreemiumPlan(),
    [DoppeFreemiumPlan.FreeTrial14]: doppeFreemiumPlanFeaturesStatusCreateForMultiplePackagesFreemiumPlan(),
    [DoppeFreemiumPlan.FreeTrial14NoFreeVersion]: doppeFreemiumPlanFeaturesStatusCreateForMultiplePackageFreemiumPlanNoFreeVersion(),
    [DoppeFreemiumPlan.FreeTrial14WithViewerDisabled]: doppeFreemiumPlanFeaturesStatusCreateForMultiplePackageFreemiumPlanNoFreeVersion(),
    [DoppeFreemiumPlan.SiteFreeTrial14]: doppeFreemiumPlanFeaturesStatusCreateForMultiplePackagesFreemiumPlan(),
    [DoppeFreemiumPlan.SiteBusiness]: doppeFreemiumPlanFeaturesStatusCreateForMultiplePackagesFreemiumPlan(),
} satisfies {
    [plan in DoppeFreemiumPlan.Enabled]: DoppePremiumFeaturesStatus
}

export function doppeFreemiumPlanGetPremiumFeaturesStatus (
    freemiumPlan: DoppeFreemiumPlan.Enabled
) : DoppePremiumFeaturesStatus {
    return DoppeFreemiumPlanFeaturesStatus[freemiumPlan]
}

export function doppeFreemiumPlanGetSupportedPremiumProducts (freemiumPlan: DoppeFreemiumPlan) {

    if (!doppeFreemiumPlanIsEnabled(freemiumPlan)) {
        return [];
    }

    switch (freemiumPlan) {
        case DoppeFreemiumPlan.GoogleMultiplePackages:
        case DoppeFreemiumPlan.MultiplePackages:
        case DoppeFreemiumPlan.MultiplePackagesOld:
        case DoppeFreemiumPlan.FreemiumTesting: {
            return [DoppePremiumProducts.Tier1, DoppePremiumProducts.Tier2, DoppePremiumProducts.Tier3];
        }
        case DoppeFreemiumPlan.Isracard: {
            return [DoppePremiumProducts.Tier1, DoppePremiumProducts.Tier2, DoppePremiumProducts.Tier3, DoppePremiumProducts.IsracardVip];
        }
        case DoppeFreemiumPlan.FreeTrial14:
        case DoppeFreemiumPlan.FreeTrial14WithViewerDisabled:
        case DoppeFreemiumPlan.FreeTrial14NoFreeVersion: {
            return [DoppePremiumProducts.FreeTrial14DaysBasic, DoppePremiumProducts.FreeTrial14DaysUltimate, DoppePremiumProducts.FreeTrial14DaysVIP]
        }
        case DoppeFreemiumPlan.SiteFreeTrial14: {
            return [DoppePremiumProducts.SiteFreeTrialDaysBasic, DoppePremiumProducts.SiteFreeTrialDaysUltimate, DoppePremiumProducts.SiteFreeTrialDaysVIP];
        }
        case DoppeFreemiumPlan.SiteBusiness: {
            return [DoppePremiumProducts.SiteFreeTrialDaysBasic, DoppePremiumProducts.SiteFreeTrialDaysUltimate, DoppePremiumProducts.WixBusinessTest, DoppePremiumProducts.WixBusinessEliteTest];
        }
        case DoppeFreemiumPlan.Default:
        case DoppeFreemiumPlan.Legacy:
        case DoppeFreemiumPlan.GoogleOnePlan: {
            return [DoppePremiumProducts.Pro];
        }
    }
}

export function doppeFreemiumPlanGetSupportedPremiumProductsForUpgrade (freemiumPlan: DoppeFreemiumPlan) {

    if (!doppeFreemiumPlanIsEnabled(freemiumPlan)) {
        return [];
    }

    switch (freemiumPlan) {
        case DoppeFreemiumPlan.GoogleMultiplePackages:
        case DoppeFreemiumPlan.MultiplePackages:
        case DoppeFreemiumPlan.MultiplePackagesOld:
        case DoppeFreemiumPlan.FreemiumTesting: {
            return [DoppePremiumProducts.Tier3, DoppePremiumProducts.Tier2, DoppePremiumProducts.Tier1];
        }
        case DoppeFreemiumPlan.Isracard: {
            return [DoppePremiumProducts.Tier3, DoppePremiumProducts.Tier2, DoppePremiumProducts.Tier1];
        }
        case DoppeFreemiumPlan.FreeTrial14WithViewerDisabled:
        case DoppeFreemiumPlan.FreeTrial14NoFreeVersion:
        case DoppeFreemiumPlan.FreeTrial14: {
            return [DoppePremiumProducts.FreeTrial14DaysVIP, DoppePremiumProducts.FreeTrial14DaysUltimate, DoppePremiumProducts.FreeTrial14DaysBasic]
        }
        case DoppeFreemiumPlan.SiteFreeTrial14: {
            return [DoppePremiumProducts.SiteFreeTrialDaysVIP, DoppePremiumProducts.SiteFreeTrialDaysUltimate, DoppePremiumProducts.SiteFreeTrialDaysBasic];
        }
        case DoppeFreemiumPlan.SiteBusiness: {
            return [DoppePremiumProducts.WixBusinessEliteTest, DoppePremiumProducts.WixBusinessTest, DoppePremiumProducts.SiteFreeTrialDaysUltimate, DoppePremiumProducts.SiteFreeTrialDaysBasic];
        }
        case DoppeFreemiumPlan.Default:
        case DoppeFreemiumPlan.Legacy:
        case DoppeFreemiumPlan.GoogleOnePlan: {
            return [DoppePremiumProducts.Pro];
        }
    }
}

export function doppeFreemiumPlanGetTiersOrderedByContaintedFeatures(freemiumPlan: DoppeFreemiumPlan) {

    if (!doppeFreemiumPlanIsEnabled(freemiumPlan)) {
        return [];
    }

    switch (freemiumPlan) {
        case DoppeFreemiumPlan.GoogleMultiplePackages:
        case DoppeFreemiumPlan.MultiplePackages:
        case DoppeFreemiumPlan.MultiplePackagesOld:
        case DoppeFreemiumPlan.FreemiumTesting: {
            return [DoppePremiumProducts.Tier1, DoppePremiumProducts.Tier2, DoppePremiumProducts.Tier3];
        }
        case DoppeFreemiumPlan.Isracard: {
            return [DoppePremiumProducts.Tier1, DoppePremiumProducts.Tier2, DoppePremiumProducts.Tier3, DoppePremiumProducts.IsracardVip];
        }
        case DoppeFreemiumPlan.FreeTrial14WithViewerDisabled:
        case DoppeFreemiumPlan.FreeTrial14NoFreeVersion:
        case DoppeFreemiumPlan.FreeTrial14: {
            return [DoppePremiumProducts.FreeTrial14DaysBasic, DoppePremiumProducts.FreeTrial14DaysUltimate, DoppePremiumProducts.FreeTrial14DaysVIP]
        }
        case DoppeFreemiumPlan.SiteFreeTrial14: {
            return [DoppePremiumProducts.SiteFreeTrialDaysBasic, DoppePremiumProducts.SiteFreeTrialDaysUltimate, DoppePremiumProducts.SiteFreeTrialDaysVIP];
        }
        case DoppeFreemiumPlan.SiteBusiness: {
            return [DoppePremiumProducts.SiteFreeTrialDaysBasic, DoppePremiumProducts.SiteFreeTrialDaysUltimate, DoppePremiumProducts.WixBusinessTest, DoppePremiumProducts.WixBusinessEliteTest];
        }
        case DoppeFreemiumPlan.Default:
        case DoppeFreemiumPlan.Legacy:
        case DoppeFreemiumPlan.GoogleOnePlan: {
            return [DoppePremiumProducts.Pro];
        }
    }
}


export function doppeFreemiumPlanCalculateMainPremiumProductForUpgrade (freemiumPlan: DoppeFreemiumPlan, premiumProductsPricing: WixPremiumProductsPricing<DoppePremiumProducts, DoppePremiumBillingCycles>) : DoppePremiumProducts | null {

    if (!doppeFreemiumPlanIsEnabled(freemiumPlan)) {
        return null;
    }

    switch (freemiumPlan) {
        case DoppeFreemiumPlan.GoogleMultiplePackages:
        case DoppeFreemiumPlan.MultiplePackages:
        case DoppeFreemiumPlan.MultiplePackagesOld:
        case DoppeFreemiumPlan.FreemiumTesting:
        case DoppeFreemiumPlan.Isracard: {
            if (wixGetPremiumProductPurchasableCycles(DoppePremiumProducts.Tier2, premiumProductsPricing)) {
                return DoppePremiumProducts.Tier2;
            }
            else {
                return DoppePremiumProducts.Tier3;
            }
        }
        case DoppeFreemiumPlan.FreeTrial14WithViewerDisabled:
        case DoppeFreemiumPlan.FreeTrial14NoFreeVersion:
        case DoppeFreemiumPlan.FreeTrial14: {
            if (wixGetPremiumProductPurchasableCycles(DoppePremiumProducts.FreeTrial14DaysUltimate, premiumProductsPricing)) {
                return DoppePremiumProducts.FreeTrial14DaysUltimate;
            }
            else {
                return DoppePremiumProducts.FreeTrial14DaysVIP;
            }
        }
        case DoppeFreemiumPlan.SiteFreeTrial14: {
            if (wixGetPremiumProductPurchasableCycles(DoppePremiumProducts.SiteFreeTrialDaysUltimate, premiumProductsPricing)) {
                return DoppePremiumProducts.SiteFreeTrialDaysUltimate;
            }
            else {
                return DoppePremiumProducts.SiteFreeTrialDaysVIP;
            }
        }
        case DoppeFreemiumPlan.SiteBusiness: {
            if (wixGetPremiumProductPurchasableCycles(DoppePremiumProducts.WixBusinessTest, premiumProductsPricing)) {
                return DoppePremiumProducts.WixBusinessTest;
            }
            else {
                return DoppePremiumProducts.WixBusinessEliteTest;
            }
        }
        case DoppeFreemiumPlan.Default:
        case DoppeFreemiumPlan.Legacy:
        case DoppeFreemiumPlan.GoogleOnePlan: {
            return DoppePremiumProducts.Pro;
        }
    }
}

export function doppeFreemiumPlanIsMultiplePackagesPremiumModel (freemiumPlan: DoppeFreemiumPlan) : freemiumPlan is DoppeFreemiumPlanWithMultiplePackages {
    return enumIsEnumValue(DoppeFreemiumPlanWithMultiplePackages, freemiumPlan);
}

export function doppeFreemiumPlanIsSinglePackagePremiumModel (freemiumPlan: DoppeFreemiumPlan) : freemiumPlan is DoppeFreemiumPlanWithSinglePackage {
    return enumIsEnumValue(DoppeFreemiumPlanWithSinglePackage, freemiumPlan);
}

export function doppeFreemiumPlanIsFreeTrialPremiumModel (freemiumPlan: DoppeFreemiumPlan) : freemiumPlan is DoppeFreemiumPlanWithFreeTrial {
    return enumIsEnumValue(DoppeFreemiumPlanWithFreeTrial, freemiumPlan);
}

export function doppeFreemiumPlanIsGoogleFremiumPlan (freemiumPlan: DoppeFreemiumPlan) {
    return freemiumPlan === DoppeFreemiumPlan.GoogleOnePlan || freemiumPlan === DoppeFreemiumPlan.GoogleMultiplePackages;
}
